import { API_CONFIG, buildApiUrl } from './config';
import { collection, getDocs, doc, getDoc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from './config';

const apiCall = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.success !== false ? data.data || data : Promise.reject(new Error(data.error));
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

export const getCourses = async () => {
  try {
    return await apiCall(buildApiUrl('/courses'));
  } catch (error) {
    console.error('Error getting courses:', error);
    throw error;
  }
};

export const getCourseById = async (id) => {
  try {
    return await apiCall(buildApiUrl(`/courses/${id}`));
  } catch (error) {
    console.error('Error getting course:', error);
    throw error;
  }
};

export const addCourse = async (courseData) => {
  try {
    const result = await apiCall(buildApiUrl('/courses'), {
      method: 'POST',
      body: JSON.stringify(courseData)
    });
    return result.id || result;
  } catch (error) {
    console.error('Error adding course:', error);
    throw error;
  }
};

export const updateCourse = async (id, updates) => {
  try {
    return await apiCall(buildApiUrl(`/courses/${id}`), {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  } catch (error) {
    console.error('Error updating course:', error);
    throw error;
  }
};

export const deleteCourse = async (id) => {
  try {
    return await apiCall(buildApiUrl(`/courses/${id}`), {
      method: 'DELETE'
    });
  } catch (error) {
    console.error('Error deleting course:', error);
    throw error;
  }
};

// ==================== COURSE QUIZZES ====================

export const getCourseQuizzes = async (courseId) => {
  try {
    const snap = await getDocs(collection(db, 'CourseQuizzes'));
    const items = [];
    snap.forEach((d) => {
      const data = d.data();
      if (!courseId || data.courseId === courseId) {
        items.push({ id: d.id, ...data });
      }
    });
    items.sort((a, b) => (a.questionNumber || 0) - (b.questionNumber || 0));
    return items;
  } catch (error) {
    console.error('Error getting course quizzes:', error);
    throw error;
  }
};

export const getCourseQuizById = async (id) => {
  try {
    const d = await getDoc(doc(db, 'CourseQuizzes', id));
    if (!d.exists()) return null;
    return { id: d.id, ...d.data() };
  } catch (error) {
    console.error('Error getting course quiz:', error);
    throw error;
  }
};

export const addCourseQuiz = async (quizData) => {
  try {
    const result = await apiCall(buildApiUrl('/course-quizzes'), {
      method: 'POST',
      body: JSON.stringify(quizData)
    });
    return result.id || result;
  } catch (error) {
    console.error('Error adding course quiz:', error);
    throw error;
  }
};

export const updateCourseQuiz = async (id, updates) => {
  try {
    return await apiCall(buildApiUrl(`/course-quizzes/${id}`), {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  } catch (error) {
    console.error('Error updating course quiz:', error);
    throw error;
  }
};

export const deleteCourseQuiz = async (id) => {
  try {
    const d = await getDoc(doc(db, 'CourseQuizzes', id));
    if (!d.exists()) throw new Error('Question not found');

    const data = d.data();
    const courseId = data.courseId;
    const deletedNum = data.questionNumber;

    await deleteDoc(doc(db, 'CourseQuizzes', id));

    const snap = await getDocs(collection(db, 'CourseQuizzes'));
    const batch = writeBatch(db);
    snap.forEach((s) => {
      const q = s.data();
      if (q.courseId === courseId && q.questionNumber > deletedNum) {
        batch.update(doc(db, 'CourseQuizzes', s.id), { questionNumber: q.questionNumber - 1 });
      }
    });
    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error('Error deleting course quiz:', error);
    throw error;
  }
};
